import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, X } from 'lucide-react';
import { useMusic } from '../../context/MusicContext';

export default function MusicPlayer() {
  const {
    currentTrack,
    playlist,
    isPlaying,
    isVisible,
    currentTime,
    duration,
    togglePlay,
    skipTrack,
    closePlayer,
    audioContextRef,
    analyserRef,
    dataArrayRef,
    initAudioContext,
  } = useMusic();

  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Initialize audio context for visualizer when user interacts with player
  const handlePlayerInteraction = () => {
    if (!audioContextRef.current) {
      initAudioContext();
    } else if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };

  // Draw mini visualizer
  useEffect(() => {
    if (!isPlaying || !canvasRef.current || !analyserRef.current) {
      if (canvasRef.current) {
        const canvasCtx = canvasRef.current.getContext('2d');
        canvasCtx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      if (analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        canvasCtx.fillStyle = 'rgb(0, 0, 0)';
        canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

        const barWidth = (WIDTH / dataArrayRef.current.length) * 1.5;
        let x = 0;

        for (let i = 0; i < dataArrayRef.current.length; i++) {
          const barHeight = (dataArrayRef.current[i] / 255) * HEIGHT * 0.8;

          const gradient = canvasCtx.createLinearGradient(0, HEIGHT - barHeight, 0, HEIGHT);
          gradient.addColorStop(0, '#f97316');
          gradient.addColorStop(1, '#dc2626');

          canvasCtx.fillStyle = gradient;
          canvasCtx.fillRect(x, HEIGHT - barHeight, barWidth - 1, barHeight);

          x += barWidth + 1;
        }
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, analyserRef, dataArrayRef]);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!isVisible || !currentTrack) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        drag
        dragConstraints={{ top: -500, left: -500, right: 0, bottom: 0 }}
        dragElastic={0.1}
      >
        {/* Retro-styled player */}
        <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-full shadow-2xl border-4 border-gray-300 p-4 min-w-[320px]">
          {/* Close button */}
          <button
            onClick={closePlayer}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="flex items-center gap-3">
            {/* Vinyl disc with cover image */}
            <div className="relative flex-shrink-0">
              <motion.div
                className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-800 to-black border-2 border-gray-600 shadow-inner relative overflow-hidden"
                animate={{ rotate: isPlaying ? 360 : 0 }}
                transition={{ duration: 3, repeat: isPlaying ? Infinity : 0, ease: 'linear' }}
              >
                {/* Cover image or vinyl look */}
                {currentTrack.coverImage ? (
                  <img
                    src={currentTrack.coverImage}
                    alt={currentTrack.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <>
                    {/* Vinyl grooves */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full border-2 border-gray-700"></div>
                      <div className="absolute w-8 h-8 rounded-full border-2 border-gray-600"></div>
                      <div className="absolute w-4 h-4 rounded-full bg-gradient-to-br from-orange-500 to-red-600"></div>
                    </div>
                  </>
                )}
                {/* Center label */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-black"></div>
                  </div>
                </div>
              </motion.div>
              {/* Tonearm */}
              <motion.div
                className="absolute -right-1 top-0 origin-top-right"
                animate={{ rotate: isPlaying ? 15 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-1 h-8 bg-gradient-to-b from-gray-700 to-gray-500 rounded-full shadow-md"></div>
              </motion.div>
            </div>

            {/* Track info and controls */}
            <div className="flex-1 min-w-0">
              {/* Track info */}
              <div className="mb-2">
                <div className="text-xs font-bold text-gray-800 truncate">
                  {currentTrack.title}
                </div>
                <div className="text-[10px] text-gray-600 truncate">
                  Duration: {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => skipTrack(-1)}
                  disabled={playlist.length <= 1}
                  className="w-7 h-7 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition-colors shadow-md"
                >
                  <SkipBack className="w-3 h-3" fill="white" />
                </button>
                <button
                  onClick={() => {
                    handlePlayerInteraction();
                    togglePlay();
                  }}
                  className="w-9 h-9 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-full flex items-center justify-center text-white transition-all shadow-lg"
                >
                  {isPlaying ? <Pause className="w-4 h-4" fill="white" /> : <Play className="w-4 h-4 ml-0.5" fill="white" />}
                </button>
                <button
                  onClick={() => skipTrack(1)}
                  disabled={playlist.length <= 1}
                  className="w-7 h-7 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition-colors shadow-md"
                >
                  <SkipForward className="w-3 h-3" fill="white" />
                </button>

                {/* Mini visualizer */}
                <div className="flex-1 h-7 bg-black rounded-lg overflow-hidden border border-gray-600 shadow-inner">
                  <canvas
                    ref={canvasRef}
                    width="120"
                    height="28"
                    className="w-full h-full"
                  />
                </div>
              </div>

              {/* Progress bar */}
              <div className="relative h-2 bg-gray-400 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-500 to-red-600 rounded-full"
                  style={{ width: `${progress}%` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
